/**
 * AI upload init for Zalify theme — tinypresent API.
 * Couple mode: 2 user photos → 1 AI generation.
 */
(function () {
  "use strict";

  const UPLOADER_DEFAULTS = {
    jobNum: 1,
    pollInterval: 2000,
    maxPollAttempts: 60,
  };

  const SECTION_SELECTORS = ".ai-upload-section, #ai-upload-section";

  function waitForDeps(callback, attempts = 0) {
    if (
      typeof AiImageUploader !== "undefined" &&
      window.aiUploadModal &&
      window.AiProductMedia &&
      window.AiGenerationHistory
    ) {
      callback();
      return;
    }
    if (attempts > 80) {
      console.error("[AI Upload] Required AI upload modules not loaded");
      return;
    }
    setTimeout(() => waitForDeps(callback, attempts + 1), 100);
  }

  function getVariantMetafields(root) {
    const container =
      root.closest(".product-form, .quick-buy-product, quick-buy-drawer, .drawer__content") ||
      root;
    const script =
      container.querySelector(".variant-metafields-data") ||
      document.querySelector(".variant-metafields-data");
    if (!script) return {};
    try {
      const data = JSON.parse(script.textContent);
      return data.variantMetafields || {};
    } catch {
      return {};
    }
  }

  function variantNeedsDualUpload(variantId, metafields) {
    const raw = metafields[variantId]?.isdouble;
    if (!raw) return false;
    const value =
      typeof raw === "object" && raw.value !== undefined ? raw.value : raw;
    const lower = String(value).toLowerCase();
    return lower === "yes" || lower === "true" || value === true;
  }

  function clearSecondUploader(root) {
    if (root._couplePendingFiles) {
      root._couplePendingFiles[2] = null;
    }

    const uploader2 = root._aiUploaders?.[2];
    if (uploader2?._localPreviewUrl) {
      URL.revokeObjectURL(uploader2._localPreviewUrl);
      uploader2._localPreviewUrl = null;
    }

    const hidden2 = root.querySelector("#ai-user-image-url-2");
    const trigger2 = root.querySelector("#ai-upload-trigger-2");
    const preview2 = root.querySelector("#ai-upload-preview-2");
    const status2 = root.querySelector("#ai-upload-status-2");
    const fileInput2 = root.querySelector("#ai-file-input-2");

    if (hidden2) hidden2.value = "";
    if (fileInput2) fileInput2.value = "";
    if (trigger2) {
      trigger2.classList.remove("has-image", "validation-error");
      delete trigger2.dataset.pendingPhoto;
    }
    if (preview2) {
      if (preview2.src?.startsWith("blob:")) {
        URL.revokeObjectURL(preview2.src);
      }
      preview2.src = "";
    }
    if (status2) {
      status2.textContent = "";
      status2.className = "ai-upload-status";
    }
  }

  function toggleSecondUploader(root, show) {
    const container1 = root.querySelector(
      '.ai-upload-container[data-upload-index="1"]'
    );
    const container2 = root.querySelector(
      '.ai-upload-container[data-upload-index="2"]'
    );

    if (container1) {
      container1.style.display = "flex";
    }
    if (container2) {
      container2.style.display = show ? "flex" : "none";
    }

    root.dataset.coupleMode = show ? "true" : "false";

    if (!show && root.dataset.aiHistoryRestoring !== "true") {
      clearSecondUploader(root);
    }
  }

  function getFormForRoot(root) {
    const formId = root.dataset.formId;
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId) return byId;
    }
    const hidden = root.querySelector("#ai-custom-image-url");
    const linkedId = hidden?.getAttribute("form");
    if (linkedId) {
      const linked = document.getElementById(linkedId);
      if (linked) return linked;
    }
    return (
      root.closest("form[id*='product-form']") ||
      root.closest(".product-form, quick-buy-drawer")?.querySelector(
        'form[id*="product-form"]'
      )
    );
  }

  function bindRemoveButton(removeBtn, preview, trigger, hidden, status, root, index) {
    if (!removeBtn || removeBtn.dataset.bound === "true") return;
    removeBtn.dataset.bound = "true";
    removeBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      const uploader = root?._aiUploaders?.[index];
      if (uploader?._localPreviewUrl) {
        URL.revokeObjectURL(uploader._localPreviewUrl);
        uploader._localPreviewUrl = null;
      }

      const fileInput = root.querySelector(
        index === 2 ? "#ai-file-input-2" : "#ai-file-input"
      );
      if (fileInput) fileInput.value = "";

      if (preview?.src?.startsWith("blob:")) {
        URL.revokeObjectURL(preview.src);
      }
      if (preview) preview.src = "";
      if (trigger) {
        trigger.classList.remove("has-image", "validation-error");
        delete trigger.dataset.pendingPhoto;
      }
      if (hidden) hidden.value = "";
      if (status) {
        status.textContent = "";
        status.className = "ai-upload-status";
      }
      if (root?._couplePendingFiles && index) {
        root._couplePendingFiles[index] = null;
      }
      const aiHidden = root.querySelector("#ai-custom-image-url");
      if (aiHidden) aiHidden.value = "";
      const taskHidden = root.querySelector("#ai-task-id");
      if (taskHidden) taskHidden.value = "";
    });
  }

  function applySelectedAiImage(root, url, entry = null) {
    const aiHidden = root.querySelector("#ai-custom-image-url");
    const taskHidden = root.querySelector("#ai-task-id");
    const status1 = root.querySelector("#ai-upload-status");
    const status2 = root.querySelector("#ai-upload-status-2");

    if (aiHidden) aiHidden.value = url || "";
    if (taskHidden && entry?.taskId) {
      taskHidden.value = String(entry.taskId);
    }

    [status1, status2].forEach((statusEl) => {
      if (!statusEl) return;
      statusEl.textContent = "✓ Design selected!";
      statusEl.className = "ai-upload-status success";
    });
  }

  function getUserImageUrlsFromRoot(root) {
    const url1 = root.querySelector("#ai-user-image-url")?.value?.trim() || "";
    const url2 =
      root.querySelector("#ai-user-image-url-2")?.value?.trim() || "";
    const urls = [url1, url2].filter(Boolean);
    return urls.length ? urls : [];
  }

  function restoreUserImageUrls(root, urls) {
    if (!root || !Array.isArray(urls) || !urls.length) return;

    const userHidden1 = root.querySelector("#ai-user-image-url");
    const userHidden2 = root.querySelector("#ai-user-image-url-2");
    const trigger1 = root.querySelector("#ai-upload-trigger");
    const trigger2 = root.querySelector("#ai-upload-trigger-2");
    const preview1 = root.querySelector("#ai-upload-preview");
    const preview2 = root.querySelector("#ai-upload-preview-2");

    if (urls[0] && userHidden1) userHidden1.value = urls[0];
    if (urls[1] && userHidden2) userHidden2.value = urls[1];

    if (root._couplePendingFiles) {
      root._couplePendingFiles = { 1: null, 2: null };
    }

    const applyPreview = (preview, trigger, url, uploader) => {
      if (!url) return;

      if (uploader?._localPreviewUrl) {
        URL.revokeObjectURL(uploader._localPreviewUrl);
        uploader._localPreviewUrl = null;
      }

      if (preview) {
        if (preview.src?.startsWith("blob:")) {
          URL.revokeObjectURL(preview.src);
        }
        preview.src = url;
      }

      if (trigger) {
        trigger.classList.add("has-image");
        trigger.classList.remove("validation-error");
        delete trigger.dataset.pendingPhoto;
      }
    };

    applyPreview(preview1, trigger1, urls[0], root._aiUploaders?.[1]);
    applyPreview(preview2, trigger2, urls[1], root._aiUploaders?.[2]);

    const status1 = root.querySelector("#ai-upload-status");
    const status2 = root.querySelector("#ai-upload-status-2");
    if (urls[0] && status1 && !status1.textContent.includes("Design selected")) {
      status1.textContent = "✓ Photo restored";
      status1.className = "ai-upload-status success";
    }
    if (urls[1] && status2 && !status2.textContent.includes("Design selected")) {
      status2.textContent = "✓ Photo restored";
      status2.className = "ai-upload-status success";
    }

    if (urls.length >= 2) {
      toggleSecondUploader(root, true);
    }
  }

  function waitMs(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function restoreHistoryEntry(root, entry) {
    if (!root || !entry?.url) return;

    root.dataset.aiHistoryRestoring = "true";
    try {
      if (typeof window.applyAiGenerationContext === "function") {
        await window.applyAiGenerationContext(root, entry);
      }

      const userImageUrls = Array.isArray(entry.userImageUrls)
        ? entry.userImageUrls.filter(Boolean)
        : [];
      const metafields = getVariantMetafields(root);

      if (userImageUrls.length >= 2) {
        toggleSecondUploader(root, true);
      } else if (entry.variantId) {
        toggleSecondUploader(
          root,
          variantNeedsDualUpload(String(entry.variantId), metafields)
        );
      }

      // 等待变体 option change 触发的 deferred applyVariant（150ms）全部完成
      await waitMs(400);

      applySelectedAiImage(root, entry.url, entry);
      if (userImageUrls.length) {
        restoreUserImageUrls(root, userImageUrls);
      }

      closeHistoryModal();
      await handleAiImageReady(root, entry.url, { saveHistory: false });
      scrollToProductMediaAfterConfirm(root);
    } finally {
      delete root.dataset.aiHistoryRestoring;
    }
  }

  function handleAiImageReady(root, url, { saveHistory = true } = {}) {
    if (!url) return Promise.resolve(null);

    const productId = window.AiGenerationHistory.getProductIdFromSection(root);
    const context =
      typeof window.getAiGenerationContext === "function"
        ? window.getAiGenerationContext(root)
        : null;

    if (saveHistory && productId) {
      const userImageUrls = getUserImageUrlsFromRoot(root);
      const taskId = root.querySelector("#ai-task-id")?.value?.trim() || "";
      window.AiGenerationHistory.add(url, productId, {
        ...context,
        userImageUrls,
        taskId,
      });
    }

    return window.AiProductMedia.addImageToProductMedia(url, {
      select: true,
      root,
    });
  }

  function scrollToProductMediaAfterConfirm(root) {
    if (!window.matchMedia("(max-width: 999px)").matches) return;
    if (typeof window.AiProductMedia?.scrollToProductMedia === "function") {
      window.AiProductMedia.scrollToProductMedia(root);
    }
  }

  function ensureHistoryModal() {
    let modal = document.getElementById("ai-generation-history-modal");
    if (modal) return modal;

    modal = document.createElement("div");
    modal.id = "ai-generation-history-modal";
    modal.className = "ai-history-modal";
    modal.hidden = true;
    modal.innerHTML = `
      <div class="ai-history-modal__backdrop" data-ai-history-close></div>
      <div class="ai-history-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="ai-history-modal-title">
        <div class="ai-history-modal__header">
          <h2 id="ai-history-modal-title" class="ai-history-modal__title">Generation History</h2>
          <button type="button" class="ai-history-modal__close" data-ai-history-close aria-label="Close">&times;</button>
        </div>
        <div class="ai-history-modal__body">
          <div class="ai-history-modal__grid" data-ai-history-grid></div>
          <p class="ai-history-modal__empty" data-ai-history-empty hidden>No history yet. Your generated designs will be saved here after you upload a photo.</p>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    modal.addEventListener("click", (e) => {
      if (e.target.closest("[data-ai-history-close]")) {
        closeHistoryModal();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !modal.hidden) {
        closeHistoryModal();
      }
    });

    return modal;
  }

  function closeHistoryModal() {
    const modal = document.getElementById("ai-generation-history-modal");
    if (modal) modal.hidden = true;
    document.body.classList.remove("ai-history-modal-open");
  }

  function openHistoryModal(root, uploadIndex) {
    const modal = ensureHistoryModal();
    const productId = window.AiGenerationHistory.getProductIdFromSection(root);
    const grid = modal.querySelector("[data-ai-history-grid]");
    const empty = modal.querySelector("[data-ai-history-empty]");
    const entries = productId
      ? window.AiGenerationHistory.getForProduct(productId)
      : [];

    modal.dataset.uploadIndex = String(uploadIndex);
    modal.dataset.sectionRoot = root.id || "ai-upload-section";

    if (!grid || !empty) return;

    grid.innerHTML = "";

    if (!entries.length) {
      empty.hidden = false;
    } else {
      empty.hidden = true;
      entries.forEach((entry) => {
        const item = document.createElement("button");
        item.type = "button";
        item.className = "ai-history-modal__item";
        item.innerHTML = `<img src="${entry.url}" alt="Generated design" loading="lazy" width="300" height="300" />`;
        item.addEventListener("click", () => {
          restoreHistoryEntry(root, entry);
        });
        grid.appendChild(item);
      });
    }

    modal.hidden = false;
    document.body.classList.add("ai-history-modal-open");
  }

  function bindHistoryTrigger(root) {
    const trigger = root.querySelector("[data-ai-history-trigger]");
    if (!trigger || trigger.dataset.bound === "true") return;
    trigger.dataset.bound = "true";

    trigger.addEventListener("click", () => {
      openHistoryModal(root, 1);
    });
  }

  function createUploader(root, index) {
    const suffix = index === 2 ? "-2" : "";
    const trigger = root.querySelector(`#ai-upload-trigger${suffix}`);
    const input = root.querySelector(`#ai-file-input${suffix}`);
    const hidden = root.querySelector(
      index === 2 ? "#ai-user-image-url-2" : "#ai-user-image-url"
    );
    const preview = root.querySelector(`#ai-upload-preview${suffix}`);
    const status = root.querySelector(`#ai-upload-status${suffix}`);
    const removeBtn = root.querySelector(`#ai-upload-remove${suffix}`);

    if (!trigger || !input || !hidden || trigger.dataset.uploaderInitialized === "true") {
      return null;
    }

    trigger.dataset.uploaderInitialized = "true";

    const uploader = new AiImageUploader({
      ...UPLOADER_DEFAULTS,
      onImageGenerated: (url) => {
        handleAiImageReady(root, url);
      },
      onImageSelect: (url) => {
        applySelectedAiImage(root, url);
        handleAiImageReady(root, url, { saveHistory: true }).then(() => {
          scrollToProductMediaAfterConfirm(root);
        });
      },
      onUploadError: (error) => {
        if (status) {
          status.textContent =
            "✗ Upload failed: " + (error?.message || "Unknown error");
          status.className = "ai-upload-status error";
        }
      },
    });

    uploader.init(trigger, input, hidden);
    bindRemoveButton(removeBtn, preview, trigger, hidden, status, root, index);
    return uploader;
  }

  function validateCustomFields(root) {
    if (typeof window.validateAiLineItemProperties !== "function") {
      return true;
    }

    const scope =
      root.closest(".product-form, .product, quick-buy-drawer, .drawer__content") ||
      root;
    const result = window.validateAiLineItemProperties(scope);

    if (result.valid) {
      return true;
    }

    const firstError = result.errors[0];
    firstError?.field?.scrollIntoView({ behavior: "smooth", block: "center" });

    if (firstError?.control instanceof HTMLElement) {
      if (typeof firstError.control.reportValidity === "function") {
        firstError.control.reportValidity();
      } else {
        firstError.control.focus({ preventScroll: true });
      }
    }

    return false;
  }

  function validateSection(root) {
    if (!validateCustomFields(root)) {
      return false;
    }

    if (root.dataset.requiresUpload !== "true") {
      return true;
    }

    const coupleMode = root.dataset.coupleMode === "true";
    const aiHidden = root.querySelector("#ai-custom-image-url");
    const userHidden1 = root.querySelector("#ai-user-image-url");
    const userHidden2 = root.querySelector("#ai-user-image-url-2");
    const trigger1 = root.querySelector("#ai-upload-trigger");
    const trigger2 = root.querySelector("#ai-upload-trigger-2");

    const hasAi = aiHidden && aiHidden.value.trim() !== "";
    const hasUser1 = userHidden1 && userHidden1.value.trim() !== "";
    const hasUser2 = coupleMode
      ? userHidden2 && userHidden2.value.trim() !== ""
      : true;

    if (trigger1) trigger1.classList.remove("validation-error");
    if (trigger2) trigger2.classList.remove("validation-error");

    if (!hasAi || !hasUser1) {
      if (trigger1) trigger1.classList.add("validation-error");
      root
        .querySelector('.ai-upload-container[data-upload-index="1"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }

    if (coupleMode && !hasUser2) {
      if (trigger2) trigger2.classList.add("validation-error");
      root
        .querySelector('.ai-upload-container[data-upload-index="2"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }

    return true;
  }

  function patchFormValidation(root, form) {
    if (!form || form.dataset.aiUploadPatched === "true") return;

    form.dataset.aiUploadPatched = "true";
    const originalCheckValidity = form.checkValidity.bind(form);

    form.checkValidity = function () {
      if (!originalCheckValidity()) return false;
      return validateSection(root);
    };
  }

  function interceptPaymentButton(root, form) {
    if (!form || form.dataset.paymentObserverAdded === "true") return;

    form.dataset.paymentObserverAdded = "true";

    function validateImages() {
      return validateSection(root);
    }

    const paymentButtonContainer = form.querySelector(".shopify-payment-button");
    if (paymentButtonContainer) {
      paymentButtonContainer.addEventListener(
        "click",
        function (e) {
          if (!validateImages()) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }
        },
        true
      );
    }

    form.addEventListener(
      "submit",
      function (e) {
        const clickedButton = e.submitter;
        if (clickedButton && !clickedButton.id?.includes("AddToCart")) {
          if (!validateImages()) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }
        }
      },
      true
    );

    const addToCartButtons = form.querySelectorAll(
      'button[type="submit"], button[name="add"], .product-form__add-button'
    );
    addToCartButtons.forEach((button) => {
      if (button.dataset.validationListenerAdded === "true") return;
      button.dataset.validationListenerAdded = "true";
      button.addEventListener(
        "click",
        function (e) {
          if (!validateImages()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
          }
        },
        true
      );
    });
  }

  function setupVariantListener(root, form, metafields) {
    if (root.dataset.variantListener === "true") return;
    root.dataset.variantListener = "true";

    function applyVariant(variantId) {
      if (!variantId || root.dataset.aiHistoryRestoring === "true") return;
      toggleSecondUploader(
        root,
        variantNeedsDualUpload(String(variantId), metafields)
      );
    }

    const variantInput = form?.querySelector('input[name="id"]');
    if (variantInput?.value) {
      applyVariant(variantInput.value);
    }

    if (variantInput) {
      variantInput.addEventListener("change", () =>
        applyVariant(variantInput.value)
      );

      let lastVariantId = variantInput.value;
      setInterval(() => {
        if (variantInput.value !== lastVariantId) {
          lastVariantId = variantInput.value;
          applyVariant(lastVariantId);
        }
      }, 400);
    }

    const container =
      form?.closest(".product-form, .quick-buy-product, quick-buy-drawer, .drawer__content") ||
      root;
    container
      ?.querySelectorAll('input[name^="option"], select[name^="option"]')
      .forEach((input) => {
        input.addEventListener("change", () => {
          setTimeout(() => applyVariant(variantInput?.value), 150);
        });
      });
  }

  function initSection(root) {
    if (!root || root.dataset.aiUploadInitialized === "true") return;
    root.dataset.aiUploadInitialized = "true";

    const metafields = getVariantMetafields(root);
    const initialCouple = root.dataset.initialCouple === "true";
    toggleSecondUploader(root, initialCouple);

    const uploader1 = createUploader(root, 1);
    const uploader2 = root.querySelector("#ai-upload-trigger-2")
      ? createUploader(root, 2)
      : null;
    root._aiUploaders = { 1: uploader1, 2: uploader2 };

    bindHistoryTrigger(root);

    const form = getFormForRoot(root);
    patchFormValidation(root, form);
    interceptPaymentButton(root, form);
    setupVariantListener(root, form, metafields);

    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput?.value) {
        toggleSecondUploader(
          root,
          variantNeedsDualUpload(variantInput.value, metafields)
        );
      }
    }
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTORS).forEach(initSection);
  }

  function setupDrawerGuard() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "open") {
          const drawer = mutation.target;
          const isOpen = drawer.hasAttribute("open");
          const uploadInProgress = drawer.dataset.aiUploadInProgress;
          const modalOpen = drawer.dataset.modalOpen;
          if (!isOpen && (modalOpen === "true" || uploadInProgress === "true")) {
            setTimeout(() => drawer.setAttribute("open", ""), 0);
          }
        }
      });
    });

    document.querySelectorAll("quick-buy-drawer").forEach((drawer) => {
      observer.observe(drawer, { attributes: true, attributeFilter: ["open"] });
    });

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.tagName === "QUICK-BUY-DRAWER") {
            observer.observe(node, { attributes: true, attributeFilter: ["open"] });
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function pollDrawers() {
    setInterval(() => {
      document.querySelectorAll("quick-buy-drawer[open]").forEach((drawer) => {
        const uploadSection = drawer.querySelector(SECTION_SELECTORS);
        if (uploadSection && uploadSection.dataset.aiUploadInitialized !== "true") {
          initSection(uploadSection);
        }
      });
    }, 500);
  }

  waitForDeps(() => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initAll);
    } else {
      initAll();
    }

    document.addEventListener("shopify:section:load", initAll);
    setupDrawerGuard();
    pollDrawers();
  });

  window.AiUpload = {
    initAll,
    initSection,
    validateSection,
  };
})();
